import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from "react";
import axios from "axios";
import { BrowserRouter, matchPath, Redirect, Route, Switch, useLocation } from "react-router-dom";
import Analytics from "./Utilities/Analytics";
import { ToastContainer } from "./components/Toasts";
import { Token } from "./hooks/useGlobalData";
import {
    AuthContext,
    getChatIdentifier,
    getCompanyId,
    getEnableOldChat,
    getStreamChatIdentifier,
    getToken,
    getUserId,
} from "./Auth";
import { engine, resetApp } from "./Utilities/index";
import { useSelectedStation } from "./components/StationSelector";
import { DevWarning, Tag } from "./components/UI";
import toast from "./components/Toasts/toast";
import { AccessCheck, IsGranted } from "./security";
import * as SettingsSections from "./pages/Settings/Sections";
import { Icon } from "@dspworkplace/ui";
import * as AnnouncementsSections from "./pages/Announcements/Sections";
import { StationInclude } from "./components/StationSelector/schema";
import { Provider as ChatNewProvider } from "./ChatNew/store";
import syncStorage from "./syncStorage";
import { OnboardingProvider } from "./pages/Onboarding/context";
import { apiUrl } from "./Conf";
import { useDeviceType } from "./hooks/useDeviceType";
import { ReportIssueProvider } from "./pages/Fleet/Overview/component/ReportIssueContext";
import { LoadingView } from "./components/Loading";

/**
 * Lazy Loaded Pages
 */
const Menu = lazy(() => import("./components/Menus/Main"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/Login/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const ApplyPosition = lazy(() => import("./pages/ApplyPosition"));
const ApplicationStage = lazy(() => import("./pages/ApplicationStage"));
const ThankYou = lazy(() => import("./pages/ApplyPosition/ThankYou"));
const Inspection = lazy(() => import("./pages/Inspection"));
const Profile = lazy(() => import("./pages/Profile"));
const PhoneBook = lazy(() => import("./pages/Phonebook"));
const DriverPhoneNumber = lazy(() => import("./pages/DriverPhoneNumber"));
const Chat = lazy(() => import("./ChatNew/Chat"));
const ChatDemo = lazy(() => import("./ChatNew/Demo"));
const CrispChat = lazy(() => import("./Utilities/Crisp"));
const Announcements = lazy(() => import("./pages/Announcements"));
const Scheduler = lazy(() => import("./pages/Scheduler/Week"));
const SchedulerView = lazy(() => import("./pages/Scheduler/Day"));
const SchedulerLoadOut = lazy(() => import("./pages/SchedulerLoadOut"));
const Stations = lazy(() => import("./pages/Stations"));
const Employees = lazy(() => import("./pages/Employees"));
const PayrollImport = lazy(() => import("./pages/Employees/PayrollImport/payroll"));
const EmployeeTimeclock = lazy(() => import("./pages/Employees/TimeClock"));
const StationNavigation = lazy(() => import("./pages/Stations/Navigator"));
const Skills = lazy(() => import("./pages/Skills"));
const Devices = lazy(() => import("./pages/Devices"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const RouteCommitment = lazy(() => import("./pages/Routes/RouteCommitment"));
const ScheduleBalance = lazy(() => import("./pages/Routes/ScheduleBalance"));
const EmployeeBalance = lazy(() => import("./pages/Routes/Employee"));
const Settings = lazy(() => import("./pages/Settings"));
const Incidents = lazy(() => import("./pages/Incidents"));
const Reports = lazy(() => import("./pages/Reports"));
const DynamicRoute = lazy(() => import("./pages/Reports").then((m) => ({ default: m.DynamicRoute })));
const Dynamic = lazy(() => import("./pages/Reports/Dynamic"));
const VanNavigator = lazy(() => import("./pages/Reports/VanNavigator"));
const Mileage = lazy(() => import("./pages/Reports/VanNavigator/Mileage"));
const Insurance = lazy(() => import("./pages/Reports/VanNavigator/Insurance"));
const MaintenanceTaskTracker = lazy(() => import("./pages/Reports/VanNavigator/MaintenanceTaskTracker"));
const VehicleCount = lazy(() => import("./pages/Reports/VanNavigator/VehicleCount"));
const VehicleServiceHistory = lazy(() => import("./pages/Reports/VanNavigator/VehicleServiceHistory"));
const IncidentForm = lazy(() => import("./pages/Incidents/form"));
const WorkFlowBuilder = lazy(() => import("./pages/WorkFlowBuilders"));
const Forms = lazy(() => import("./pages/WorkFlowBuilders/Forms"));
const Workflow = lazy(() => import("./pages/WorkFlowBuilders/Workflow"));

// Scorecard
const ScorecardDrivers = lazy(() => import("./pages/Scorecard/Sections/drivers"));
const ScorecardTeams = lazy(() => import("./pages/Scorecard/Sections/teams"));
const ScorecardCoaching = lazy(() => import("./pages/Scorecard/Sections/coaching"));
const ScorecardDocuments = lazy(() => import("./pages/Scorecard/Sections/documents"));
const CompanyDocuments = lazy(() => import("./pages/CompanyDocuments/documents"));
const ScorecardDriverApp = lazy(() => import("./pages/Scorecard/Sections/app"));
const RevenueReport = lazy(() => import("./pages/Reports/Revenue"));

//Onboarding
const Applicants = lazy(() => import("./pages/Onboarding/applicants").then((m) => ({ default: m.Applicants })));
const Positions = lazy(() => import("./pages/Onboarding/positions").then((m) => ({ default: m.Positions })));
const Workflows = lazy(() => import("./pages/Onboarding/workflows").then((m) => ({ default: m.Workflows })));
const ManageWorkflow = lazy(() =>
    import("./pages/Onboarding/workflows/manage").then((m) => ({ default: m.ManageWorkflow })),
);

// FLEET
const FleetOverview = lazy(() => import("./pages/Fleet/Overview"));
const FleetManagement = lazy(() => import("./pages/Fleet/Management"));
const FleetVanAffinity = lazy(() => import("./pages/Fleet/VanAffinity"));
const VehicleDetails = lazy(() => import("./pages/Fleet/Management/Detail"));
const FleetInspections = lazy(() => import("./pages/Fleet/Inspections"));
const FleetInspectionDetails = lazy(() => import("./pages/Fleet/Inspections/Detail"));
const FleetInspectionImagesViewer = lazy(() => import("./pages/Fleet/Inspections/ImagesViewer"));
const FleetVanAutoAssignment = lazy(() => import("./pages/Fleet/VanAutoAssignment"));
const FleetMaintenance = lazy(() => import("./pages/Fleet/Maintenance"));
const FleetVehicleMaintenanceTasks = lazy(() => import("./pages/Fleet/Maintenance/tasks"));
const FleetMaintenanceTypes = lazy(() => import("./pages/Fleet/Maintenance/types"));
const FleetMaintenanceVendors = lazy(() => import("./pages/Fleet/Maintenance/vendor"));
const FleetMaintenanceTemplates = lazy(() => import("./pages/Fleet/Maintenance/template"));
const FleetMaintenanceTasksLog = lazy(() => import("./pages/Fleet/Maintenance/tasksLog"));
const FleetImage = lazy(() => import("./pages/Fleet/Images"));
const TaskTracker = lazy(() => import("./pages/Fleet/TaskTracker"));

//Lazy loading
const RouteLoadingFallback = () => <div style={{ position: 'relative', height: '100%' }}><LoadingView containerHeight='100%' imgHeight='40px' overlay={true} /></div>;

export const Organization = {
    name: "Organization",
    path: "/organization",
    isResetPasswordMenu: true,
    routes: [
        {
            name: "Stations",
            path: "/stations",
            matchPath: "/stations",
            component: Stations,
        },
        {
            name: "Stations Menu",
            path: "/station/:id",
            matchPath: "/station/:id",
            component: StationNavigation,
            renderMenu: false,
        },
        {
            name: "Skills",
            path: "/skills",
            component: Skills,
        },
        {
            name: "Team",
            path: "/employees",
            matchPath: "/employees",
            component: Employees,
            isStation: true,
        },
        {
            name: "Time Clock",
            path: "/employee/:id/time_clock",
            component: EmployeeTimeclock,
            renderMenu: false,
        },
        {
            name: "Payroll Import",
            path: "/employees-import/payroll",
            matchPath: "/employees-import/payroll",
            component: PayrollImport,
            isStation: true,
            renderMenu: false,
        },
        {
            name: "Devices",
            path: "/devices",
            component: Devices,
            isStation: true,
        },
        {
            name: "Announcements",
            path: "/announcements",
            component: Announcements,
            isStation: true,
            feature: AccessCheck.features.ANNOUNCEMENTS,
            routes: [
                { name: "Messages", path: "/messages", component: AnnouncementsSections.Messages },
                { name: "Templates", path: "/template", component: AnnouncementsSections.Template },
                //{name: "Segments",path:'/segment',component:AnnouncementsSections.Segment}
            ],
        },
        {
            name: "WorkFlow Builders",
            path: "/workflowbuilder",
            component: WorkFlowBuilder,
            isStation: true,
            renderMenu: true,
            isCompany: [85],
            routes: [
                { name: "Forms", path: "/forms", component: Forms },
                { name: "Workflow", path: "/workflow", component: Workflow },
            ],
        },
        {
            name: "Reports",
            path: "/custom-reports",
            component: Reports,
        },
        {
            name: "Documents",
            path: "/documents",
            component: CompanyDocuments,
        },
        // {
        //     name: "Time Clock",
        //     path: "/time_clock",
        //     component: TimeClock,
        // },
    ],
};

export const CustomReport = {
    name: "Reports",
    path: "/custom-reports",
    isAuthorized: true,
    component: Reports,
    renderMenu: false,
    isResetPasswordMenu: true,
    isStation: true,
    feature: AccessCheck.features.VAN_REPORT,
    routes: [
        {
            name: "Revenue Report",
            description: "See the last closed billing cycle revenue for metered billing products",
            path: "/revenue",
            matchPath: "/custom-reports/revenue",
            component: RevenueReport,
            renderMenu: false,
            isCompany: [85,1000811],
            feature: AccessCheck.features.SYS_ADMIN,
        },
        {
            name: "Van Reports",
            description: "Download your vans mileage report",
            path: "/van",
            matchPath: "/custom-reports/van",
            component: VanNavigator,
            renderMenu: false,
            feature: AccessCheck.features.VAN_REPORT,
            routes: [
                {
                    name: "Mileage",
                    path: "/mileage",
                    icon: Icon.Calendar,
                    component: Mileage,
                    default: true,
                    feature: AccessCheck.features.VAN_REPORT,
                },
                {
                    name: "Insurance",
                    path: "/insurance",
                    icon: Icon.Calendar,
                    component: Insurance,
                    default: true,
                    feature: AccessCheck.features.VAN_REPORT,
                },
                {
                    name: "Maintenance TaskTracker",
                    path: "/maintenance/task-tracker",
                    icon: Icon.Calendar,
                    component: MaintenanceTaskTracker,
                    default: true,
                    feature: AccessCheck.features.VAN_REPORT,
                },
                {
                    name: "Vehicle Usage Count",
                    path: "/vehicle-count",
                    icon: Icon.Calendar,
                    component: VehicleCount,
                    default: true,
                    feature: AccessCheck.features.VAN_REPORT,
                },
                {
                    name: "Vehicle Service History",
                    path: "/vehicle-service-history",
                    icon: Icon.Calendar,
                    component: VehicleServiceHistory,
                    default: true,
                    feature: AccessCheck.features.VAN_REPORT,
                },
            ],
        },
        {
            path: "/dynamic/:report",
            matchPath: "/custom-reports/dynamic/:report",
            isAuthorized: true,
            component: DynamicRoute,
            renderMenu: false,
            isResetPasswordMenu: true,
            isStation: false,
            isDynamic: true,
        },
        {
            path: "/dynamic/:report/:subreport",
            matchPath: "/custom-reports/dynamic/:report/:subreport",
            isAuthorized: true,
            component: Dynamic,
            renderMenu: false,
            isResetPasswordMenu: true,
            isStation: false,
            isDynamic: true,
        },
    ],
};

export const ROUTES = [
    {
        path: "/login",
        component: Login,
        useMenu: false,
        isAuthorized: false,
        view: "both",
    },
    {
        path: "/forgot-password",
        component: ForgotPassword,
        useMenu: false,
        isAuthorized: false,
        view: "both",
    },
    {
        path: "/register",
        component: Register,
        useMenu: false,
        isAuthorized: false,
    },
    {
        name: "Apply Positions",
        path: "/:companyName/:positionName/apply/:positionId",
        matchPath: "/:companyName/:positionName/apply/:positionId",
        isAuthorized: false,
        useMenu: false,
        component: ApplyPosition,
        view: "both",
    },
    {
        name: "Apply Positions",
        path: "/:companyName/apply/thank-you",
        matchPath: "/:companyName/apply/thank-you",
        isAuthorized: false,
        useMenu: false,
        component: ThankYou,
        view: "both",
    },
    {
        name: "Opening Stage",
        path: "/application/:companyName/:positionName/:positionId/:uniqueUrlId",
        matchPath: "/application/:companyName/:positionName/:positionId/:uniqueUrlId",
        isAuthorized: false,
        useMenu: false,
        component: ApplicationStage,
        view: "both",
    },
    {
        name: "Phonebook",
        path: "/phonebook",
        component: PhoneBook,
        isAuthorized: true,
        renderMenu: false,
        view: "mobile",
    },
    {
        name: "Profile",
        path: "/profile",
        component: Profile,
        isAuthorized: true,
        renderMenu: false,
        view: "mobile",
    },
    {
        name: "Inspection",
        path: "/inspection/:vehicleId?",
        component: Inspection,
        isAuthorized: true,
        renderMenu: false,
        view: "mobile",
    },
    {
        name: "Inspection Details",
        path: "/public/inspection/:inspId/:token",
        renderMenu: false,
        useMenu: false,
        isAuthorized: false,
        component: FleetInspectionDetails,
        view: "both",
    },
    {
        name: "Driver Phone Number",
        path: "/driver-phone-number",
        component: DriverPhoneNumber,
        useMenu: false,
        isAuthorized: true,
        renderMenu: false,
        view: "mobile",
    },
    {
        name: "Vehicle Details",
        path: "/vehicle/:vehicleId/:taskId?",
        matchPath: "/vehicle/:vehicleId/:taskId?",
        isAuthorized: true,
        component: VehicleDetails,
        renderMenu: false,
        view: "mobile",
    },
    {
        name: "Dashboard",
        path: "/dashboard",
        component: Dashboard,
        isAuthorized: true,
        isResetPasswordMenu: true,
        feature: AccessCheck.features.DASHBOARD,
    },
    {
        name: "Chat",
        path: "/chat",
        isAuthorized: true,
        component: Chat,
        isResetPasswordMenu: true,
        renderMenu: false,
        view: "both",
        feature: AccessCheck.features.DASHBOARD,
    },
    {
        name: "Scheduler",
        path: "/scheduler",
        isAuthorized: true,
        isStation: true,
        isResetPasswordMenu: true,
        routes: [
            {
                name: "Week",
                path: "/week",
                component: Scheduler,
                isAuthorized: false,
                renderMenu: false,
                view:'both',
            },
            {
                name: "Day",
                path: "/day",
                component: SchedulerView,
                isAuthorized: false,
                renderMenu: false,
                view:'both',
            },
            {
                name: "Load Out",
                path: "/loadout",
                isAuthorized: false,
                renderMenu: false,
                component: SchedulerLoadOut,
                view:'both',
            },
        ],
    },
    {
        name: "Routes",
        path: "/routes",
        isAuthorized: true,
        isStation: true,
        isResetPasswordMenu: true,
        routes: [
            {
                name: "Route Commitment",
                path: "/route-commitment",
                component: RouteCommitment,
                feature: AccessCheck.features.ROUTE_COMMITMENT,
            },
            {
                name: "Schedule Balance",
                path: "/schedule-balanace",
                component: ScheduleBalance,
                feature: AccessCheck.features.SCHEDULE_BALANCE,
            },
            {
                name: "Employee Balance",
                path: "/employee-balance",
                component: EmployeeBalance,
                feature: AccessCheck.features.EMPLOYEE_BALANCE,
            },
        ],
    },
    {
        name: "Incident Form",
        path: "/incidents/:id",
        matchPath: "/incidents/:id",
        isAuthorized: true,
        component: IncidentForm,
        renderMenu: false,
        feature: AccessCheck.features.INCIDENTS,
        view: "both"
    },
    {
        name: "Incidents",
        path: "/incidents",
        isAuthorized: true,
        component: Incidents,
        isStation: true,
        isResetPasswordMenu: true,
        feature: AccessCheck.features.INCIDENTS,
        view: "both"
    },
    {
        name: "Scorecard",
        path: "/scorecard",
        isAuthorized: true,
        isResetPasswordMenu: true,
        isStation: true,
        feature: AccessCheck.features.SCORECARD,
        routes: [
            // {
            //     name: <>Overall</>,
            //     path: '/overall',
            //     component: ScorecardOverall,
            // },
            {
                name: <>Coaching</>,
                path: "/coaching",
                component: ScorecardCoaching,
                feature: AccessCheck.features.SCORECARD,
            },
            {
                name: <>Teams</>,
                path: "/teams",
                component: ScorecardTeams,
                feature: AccessCheck.features.SCORECARD,
            },
            {
                name: "Driver Performance",
                path: "/driver-performance",
                component: ScorecardDrivers,
                feature: AccessCheck.features.SCORECARD,
            },
            {
                name: "Driver App",
                path: "/driver-app",
                component: ScorecardDriverApp,
                feature: AccessCheck.features.SCORECARD,
            },
            {
                name: "Documents",
                path: "/documents",
                component: ScorecardDocuments,
                feature: AccessCheck.features.SCORECARD,
            },
            // {
            //     name: "Forced Coaching Metric",
            //     path: "/forced-coaching-metric",
            //     component: AutomatedScorecardCoaching,
            //     feature: AccessCheck.features.SCORECARD,
            // },
        ],
    },
    Organization,
    CustomReport,
    {
        name: "Edit Profile",
        path: "/edit-profile",
        component: EditProfile,
        isAuthorized: true,
        renderMenu: false,
    },
    {
        name: "Settings",
        path: "/settings",
        component: Settings,
        isAuthorized: true,
        renderMenu: false,
        isResetPasswordMenu: true,
        feature: AccessCheck.features.SETTINGS,
        routes: [
            {
                name: "Company",
                routes: [
                    {
                        name: "Information",
                        path: "/company-information",
                        component: SettingsSections.CompanyInformation,
                    },
                    {
                        name: "Scheduler settings",
                        path: "/scheduler-settings",
                        component: SettingsSections.SchedulerSetting,
                    },
                    {
                        name: "Labor Compliance Alerts",
                        path: "/labor-compliance-alerts",
                        component: SettingsSections.LaborComplianceAlerts,
                    },
                    {
                        name: "Attendance settings",
                        path: "/attendance-settings",
                        component: SettingsSections.AttendanceSetting,
                    },
                    {
                        name: "Expiration settings",
                        path: "/expiration-settings",
                        component: SettingsSections.ExpirationSettings,
                    },
                ],
            },
            {
                name: "Subscription",
                routes: [
                    {
                        name: "Package",
                        path: "/package",
                        component: SettingsSections.SubscriptionPackage,
                    },
                    // {
                    //     name: "Payment Methods",
                    //     path: "/payment-methods",
                    //     component: SettingsSections.SubscriptionPaymentMethods
                    // },
                    {
                        name: "Invoices",
                        path: "/invoices",
                        component: SettingsSections.SubscriptionInvoices,
                    },
                ],
            },
            {
                name: "Integrations",
                routes: [
                    {
                        name: "RouteAssist&TRADE;",
                        path: "/routeassist",
                        component: SettingsSections.RouteAssist3Settings,
                    },
                    {
                        name: "Payroll",
                        path: "/payroll-settings",
                        component: SettingsSections.PayrollSettings,
                        feature: AccessCheck.features.PAYROLL,
                    },
                    // {
                    //     name: (
                    //         <>
                    //             ADP{" "}
                    //             <Tag small type={"warnings"} style={{ marginLeft: "1em", fontWeight: "bold" }}>
                    //                 LEGACY
                    //             </Tag>
                    //         </>
                    //     ),
                    //     path: "/adp-legacy",
                    //     component: SettingsSections.ADPLegacy,
                    //     feature: AccessCheck.features.PAYROLL,
                    // },
                    {
                        name: (
                            <>
                                Paycom{" "}
                                <Tag small type={"warnings"} style={{ marginLeft: "1em", fontWeight: "bold" }}>
                                    LEGACY
                                </Tag>
                            </>
                        ),
                        path: "/paycom-legacy",
                        component: SettingsSections.PaycomSettings,
                        feature: AccessCheck.features.PAYROLL,
                    },
                ],
            },
            {
                name: "Incidents",
                routes: [
                    {
                        name: "Incident Settings",
                        path: "/incident/setting",
                        component: SettingsSections.IncidentSettings,
                    },
                ],
            },
        ],
    },
    {
        name: "Fleet",
        path: "/fleet",
        isResetPasswordMenu: true,
        view: "both",
        routes: [
            {
                name: "Overview",
                path: "/overview",
                component: FleetOverview,
                icon: "Dashboard",
                view: "both",

            },
            {
                name: "Management",
                path: "/vehicle-management",
                component: FleetManagement,
                icon: "Truck",
                view: "both",
            },
            {
                name: "Inspections",
                path: "/inspections",
                component: FleetInspections,
            },
            {
                name: "Inspection Images",
                path: "/inspection-images/:id",
                component: FleetInspectionImagesViewer,
                renderMenu: false,
            },
            {
                name: "Inspection Details",
                path: "/inspection/:inspId",
                renderMenu: false,
                useMenu: false,
                component: FleetInspectionDetails,
                view: "both",
            },
            {
                name: "Task Tracker",
                path: "/task-tracker",
                component: TaskTracker,
                renderMenu: false,
                icon: "Notes",
                view: "mobile",
                feature: AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN,
                avoidPermission: [85, 12],
            },
            {
                name: "Images",
                path: "/vehicle-image/:id",
                component: FleetImage
            },
            {
                name: "Van Affinity",
                path: "/van-affinity",
                component: FleetVanAffinity
            },
            {
                name: "Van Auto Assignment",
                path: "/van-auto-assignment",
                component: FleetVanAutoAssignment,
            },
            {
                name: "Maintenance",
                path: "/maintenance",
                component: FleetMaintenance,
                isStation: true,
                routes: [
                    {
                        name: "TaskTracker",
                        path: "/tasks",
                        component: FleetVehicleMaintenanceTasks,
                        feature: AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN,
                    },
                    {
                        name: "Maintenance Log",
                        path: "/tasksLog",
                        component: FleetMaintenanceTasksLog,
                    },
                    {
                        name: "Maint Templates",
                        path: "/template",
                        component: FleetMaintenanceTemplates,
                        feature: AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN,
                    },
                    {
                        name: "Maint Vendors",
                        path: "/vendor",
                        feature: AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN,
                        component: FleetMaintenanceVendors,
                    },
                    {
                        name: "Types",
                        path: "/types",
                        feature: AccessCheck.addons.VEHICLE_MAINTENANCE_PLAN,
                        component: FleetMaintenanceTypes,
                    },
                ],
            },
        ],
    },
    {
        name: "Onboarding",
        path: "/onboarding",
        feature: AccessCheck.addons.ONBOARDING,
        routes: [
            {
                name: <>Applicants</>,
                path: "/applicants/:applicantId?",
                menuPath: "/applicants",
                component: Applicants,
            },
            {
                name: <>Positions</>,
                path: "/positions",
                component: Positions,
            },
            {
                name: <>Workflows</>,
                path: "/workflows",
                component: Workflows,
            },
            {
                name: "Manage Workflow",
                path: "/manage-workflow",
                component: ManageWorkflow,
                renderMenu: false,
            },
            {
                name: "Settings",
                path: "/settings",
                component: SettingsSections.OnboardingSettings,
            }
        ],
    },
];

export function useRouteByPath(path) {
    if (!path) path = window.location.pathname;

    const all = {};
    const getRoutes = (route) => {
        if (Array.isArray(route.routes))
            route.routes.forEach((subRoute) => {
                all[[route.path, subRoute.path].join("")] = subRoute;

                getRoutes(subRoute);
            });
    };

    ROUTES.forEach((route) => {
        all[route.path] = route;
        getRoutes(route);
    });

    return all[path];
}

export function renderGrantedRoute({
    feature,
    component,
    props,
    allowedPaths,
    isMobile,
    warning = true,
    loading = true,
    avoidPermission = false,
}) {
    const currentPath = props?.match?.path || props?.location?.pathname;

    if (allowedPaths && !allowedPaths.includes(currentPath)) {
        return <Redirect to={isMobile ? "/fleet/overview" : "/dashboard"} />;
    }
    const render = React.createElement(component, props);

    if (!feature) return render;

    if (avoidPermission) {
        return <>{render}</>;
    }
    return (
        <IsGranted renderWarning={warning} feature={feature} renderLoading={loading}>
            {render}
        </IsGranted>
    );
}

const LayoutRouter = ({
    changeRoute,
    isAuthorized = true,
    useMenu = true,
    path,
    component,
    isStation,
    isResetPasswordMenu,
    twilioIdentity,
    allowedPaths,
    ...rest
}) => {
    // useDeviceRotateAlert();

    const [authToken] = Token();
    const { isMobile } = useDeviceType();
    const [selected] = useSelectedStation(null);
    let containerStyle = { padding: "0px", gridRow: '3' };
    if (!useMenu) {
        containerStyle.padding = "0";
    }
    if (isMobile) {
        containerStyle.overflowX = "auto";
    }

    if (isAuthorized && !authToken) {
        return <Redirect to="/login" />;
    }

    if (isResetPasswordMenu && localStorage.getItem("isResetPassword") === "true") {
        toast({
            type: "warning",
            title: "You must change your password first then you can access other sections.",
            useClose: true,
            timeout: 10000,
        });
        return <Redirect to="/edit-profile#user-informations" />;
    }

    if (isStation === true && !selected) {
        toast({
            type: "warning",
            title: "To use any section it's necessary to add one station !!!",
            useClose: true,
            timeout: false,
        });
        return <Redirect to="/organization/stations" />;
    }
    if (
        !selected &&
        (window.location.pathname === "/organization/devices" ||
            window.location.pathname === "/organization/vehicles" ||
            window.location.pathname === "/organization/employees")
    ) {
        toast({
            type: "warning",
            title: "To use any section it's necessary to add one station !!!",
            useClose: true,
            timeout: false,
        });
        return <Redirect to="/organization/stations" />;
    }

    return (
        <div style={containerStyle}>
            <Route
                key={`route-${path}`}
                path={path}
                render={({ match: { url, path } }) => {
                    if (isAuthorized) localStorage.setItem("homepage", rest.location.pathname);
                    const isOnboardingRoute = path === "/onboarding";

                    const routeContent = (
                        <>
                            {component ? (
                                <Route
                                    path={path}
                                    exact={url === "/" || rest.routes?.some((r) => r.path)}
                                    render={(props) => (
                                        <Suspense fallback={<RouteLoadingFallback />}>
                                            {renderGrantedRoute({
                                                feature: rest.feature,
                                                component,
                                                props,
                                                allowedPaths,
                                                isMobile
                                            })}
                                        </Suspense>
                                    )}
                                />
                            ) : null}
                            {rest.routes?.map((subRoute, i) => {
                                return (
                                    <Route
                                        key={`subroute-${url}${subRoute.name ?? subRoute.path}`}
                                        path={[`${url}${subRoute.path}`]}
                                        render={(props) => (
                                            <Suspense fallback={<RouteLoadingFallback />}>
                                                {renderGrantedRoute({
                                                    feature: subRoute.feature ?? rest.feature,
                                                    component: subRoute.component,
                                                    props,
                                                    allowedPaths,
                                                    isMobile
                                                })}
                                            </Suspense>
                                        )}
                                    />
                                );
                            })}
                        </>
                    );

                    if (isOnboardingRoute) {
                        return <OnboardingProvider>{routeContent}</OnboardingProvider>;
                    }

                    return routeContent;
                }}
            />
        </div>
    );
};

const ChatApp = ({ twilioIdentity }) => {
    const location = useLocation();

    const isApplyPage =
        matchPath(location.pathname, { path: "/:companyName/:positionName/apply/:positionId" }) ||
        matchPath(location.pathname, { path: "/:companyName/apply/thank-you" }) ||
        matchPath(location.pathname, { path: "/application/:companyName/:positionName/:positionId/:uniqueUrlId" });

    if (!isApplyPage) return <ChatDemo twilioIdentity={twilioIdentity} />;
    return null;
};

const getAllPaths = (routes, parentPath = "", type) => {
    let paths = [];

    routes.forEach((route) => {
        const fullPath = `${parentPath}${route.path || ""}`;
        if (route.path && type === "mobile" && (route.view === "mobile" || route.view === "both")) paths.push(fullPath);

        if (route.path && type === "desktop" && (!route.view || route.view === "both")) paths.push(fullPath);

        if (route.routes) {
            paths = paths.concat(getAllPaths(route.routes, fullPath, type));
        }
    });

    return paths;
};

const RouteApp = () => {
    const api = engine();
    const [authData, setAuthData] = useState({ authToken: getToken(), twilioIdentity: getChatIdentifier() });
    const { twilioIdentity } = authData;
    const { isMobile } = useDeviceType();
    const mobileAllowedPaths = getAllPaths(ROUTES, "", "mobile");
    const desktopAllowedPaths = getAllPaths(ROUTES, "", "desktop");

    const setAuthToken = useCallback((value) => {
        setAuthData((prev) => ({ ...prev, authToken: value }));
    }, []);

    const setTwilioIdentity = useCallback((value) => {
        setAuthData((prev) => ({ ...prev, twilioIdentity: value }));
    }, []);

    const isValidToken = useCallback(() => {
        if (authData.authToken === "null") {
            localStorage.clear();
            window.location.reload();
            return false;
        }
        return typeof authData.authToken === "string" && authData.authToken.length > 500;
    }, [authData.authToken]);

    useEffect(() => {
        const stations = localStorage.getItem("stations") && JSON.parse(localStorage.getItem("stations"));
        const isFetchStations = !localStorage.getItem("stations") || (stations && !stations?.[0]?.timezone);
        if (isValidToken() && isFetchStations) {
            api.post("/api/lazy/manage/data", {
                actions: {
                    response: {
                        Station: {
                            custom: {
                                functionName: "getStationByRole",
                                get: "stations",
                                criteria: {
                                    company: getCompanyId(),
                                    user: getUserId(),
                                },
                                includes: StationInclude,
                            },
                        },
                    },
                },
            }).then(
                (response) => {
                    const stations = response?.data?.data?.stations || [];
                    const selectedStationFromStorage =
                        localStorage.getItem("station") && JSON.parse(localStorage.getItem("station"));
                    const selectedStation =
                        selectedStationFromStorage &&
                        stations.find((station) => station.id === selectedStationFromStorage.id);
                    if (selectedStation?.id) {
                        localStorage.setItem("station", JSON.stringify(selectedStation));
                    }
                    localStorage.setItem("stations", JSON.stringify(stations));
                },
                (error) => console.log(error)
            );
        }
    }, [api, authData.authToken, isValidToken]);

    const logout = useCallback(async () => {
        await resetApp();
    }, []);

    const CancelToken = axios.CancelToken;
    const AxiosSource = CancelToken.source();

    const ENV =
        process.env.NODE_ENV === "development" || /devapp/.exec(window.location.href) ? "development" : "production";

    if (
        localStorage.getItem("homepage")?.startsWith("/company-management")
    ) {
        localStorage.setItem(
            "homepage",
            localStorage.getItem("homepage").replace("company-management", "organization")
        );
    }

    const allowedPaths = isMobile ? mobileAllowedPaths : desktopAllowedPaths;

    if (isMobile) {
        if (syncStorage.get("deviceId") && syncStorage.get("deviceUID")) {
            localStorage.setItem("homepage", "/fleet/overview");
        } else {
            localStorage.setItem("homepage", "/driver-phone-number");
        }
    } else {
        localStorage.setItem("homepage", "/dashboard");
    }

    let homeVar =
        localStorage.getItem("currentUserRole") === "ROLE_CHAT_USER"
            ? "/chat"
            : isMobile
                ? "/fleet/overview"
                : localStorage.getItem("homepage") === "/organization/vehicles/vehicle-image/:id"
                    ? "/organization/vehicles/vehicle-image/id"
                    : localStorage.getItem("homepage");

    const contextValue = useMemo(
        () => ({
            setAuthData,
            setAuthToken,
            setTwilioIdentity,
            api,
            AxiosSource,
            logout,
            ENV,
        }),
        [setAuthData, setAuthToken, setTwilioIdentity, api, AxiosSource, logout, ENV]
    );

    const isValidTokenAndIdentity = useCallback((token, identity) => {
        if (token === "null" || identity === "null") {
            localStorage.clear();
            window.location.reload();
            return false;
        }
        return typeof token === "string" && typeof identity === "string" && token.length > 500 && identity.length > 0;
    }, []);

    const filteredRoutes = ROUTES.filter(
        (route) =>
            (localStorage.getItem("currentUserRole") !== "ROLE_CHAT_USER" ||
                (localStorage.getItem("currentUserRole") === "ROLE_CHAT_USER" &&
                    (route.path === "/chat" || route.path === "/edit-profile"))) &&
            !(route.path === "/onboarding" && apiUrl?.includes(".co.uk"))
    );

    return (
        <AuthContext.Provider value={contextValue}>
            <ReportIssueProvider>
                {ENV === "development" && <DevWarning />}
                <CrispChat />
                <BrowserRouter>
                    <ToastContainer />
                    <Analytics>
                        <ChatNewProvider>
                            <Route
                                render={({ location }) => {

                                    const currentRoute = filteredRoutes.find((route) => route.path && matchPath(location.pathname, { path: route.path }));
                                    const useMenu = currentRoute?.useMenu ?? true;

                                    return (
                                        <>
                                            {useMenu && (
                                                <Menu routes={filteredRoutes}>
                                                    {getStreamChatIdentifier() !== "" && getStreamChatIdentifier() && isValidTokenAndIdentity(authData.authToken, twilioIdentity) &&
                                                        <ChatApp twilioIdentity={twilioIdentity} />
                                                    }
                                                </Menu>
                                            )}
                                            <Switch>
                                                {isValidToken() && (
                                                    <Redirect
                                                        from="/login"
                                                        to={
                                                            localStorage.getItem("currentUserRole") === "ROLE_CHAT_USER"
                                                                ? "/chat"
                                                                : homeVar || "/dashboard"
                                                        }
                                                    />
                                                )}
                                                {filteredRoutes.map((route, i) => {
                                                    return (
                                                        <LayoutRouter key={`layoutRender-${route.path}`} {...route} twilioIdentity={twilioIdentity} allowedPaths={allowedPaths} />
                                                    )
                                                })}
                                                {isValidToken() && homeVar && <Redirect from="*" to={homeVar} />}
                                                {!isValidToken() && <Redirect from="*" to="/login" />}
                                            </Switch>
                                        </>
                                    )
                                }}
                            />
                        </ChatNewProvider>
                    </Analytics>
                </BrowserRouter>
            </ReportIssueProvider>
        </AuthContext.Provider>
    );
};

export default RouteApp;
